import { Injectable, InjectionToken } from '@angular/core';
import { defer, from, Observable, of, switchMap, take, throwError } from 'rxjs';
import type { CaptureResult } from '@zumer/snapdom';
import { DownloadFormat } from '@lib.publications/content-actions/types';
import {
  CsvExportSource,
  DocxExportSource,
  ExportPadding,
  ExportSource,
  PdfExportSource,
  PngExportSource,
  SvgExportSource,
} from '@lib.publications/content-actions/export-source';
import { imagePdf, PageSize } from '@lib.publications/content-actions/image-pdf';
import { surfaceColor } from '@lib.publications/utils/surface-color';

export interface DownloadStrategy {
  readonly fileType: DownloadFormat;
  supports(source: ExportSource): boolean;
  createBlob(source: ExportSource): Observable<Blob>;
}

export const DOWNLOAD_STRATEGY = new InjectionToken<readonly DownloadStrategy[]>('DOWNLOAD_STRATEGY');

@Injectable()
export class PngDownloadStrategy implements DownloadStrategy {
  readonly fileType: DownloadFormat = 'png';

  supports(source: ExportSource): boolean {
    return typeof (source as PngExportSource).getExportElement === 'function';
  }

  createBlob(source: ExportSource): Observable<Blob> {
    return captureExportElement(source as PngExportSource, false, (canvas: HTMLCanvasElement) => canvasBlob(canvas));
  }
}

/** A PDF of the same capture a PNG download takes, for sources the backend has no PDF for. */
@Injectable()
export class ElementPdfDownloadStrategy implements DownloadStrategy {
  readonly fileType: DownloadFormat = 'pdf';

  supports(source: ExportSource): boolean {
    return typeof (source as PngExportSource).getExportElement === 'function';
  }

  createBlob(source: ExportSource): Observable<Blob> {
    return captureExportElement(source as PngExportSource, true, (canvas: HTMLCanvasElement, page: PageSize) =>
      imagePdf(canvas, page, source.exportFileName));
  }
}

const CAPTURE_DPR = 2;
const NO_PADDING: ExportPadding = { top: 0, right: 0, bottom: 0, left: 0 };

/** A PNG at a chosen size is that size in pixels; a PDF page is that size, its image drawn at the usual density. */
function captureExportElement(
  source: PngExportSource,
  onSurface: boolean,
  render: (canvas: HTMLCanvasElement, page: PageSize) => Promise<Blob>,
): Observable<Blob> {
  const sized = source.sizedExportElement?.();
  const element$ = sized ?? defer(() => {
    const element = source.getExportElement();
    return element ? of(element) : throwError(() => new Error('No source element provided.'));
  });
  const dpr = sized && !onSurface ? 1 : CAPTURE_DPR;
  return element$.pipe(
    switchMap((element: HTMLElement) => capture(source, element, dpr, onSurface, render)),
    take(1),
  );
}

function capture(
  source: PngExportSource,
  element: HTMLElement,
  dpr: number,
  onSurface: boolean,
  render: (canvas: HTMLCanvasElement, page: PageSize) => Promise<Blob>,
): Observable<Blob> {
  const padding = source.exportPadding ?? NO_PADDING;
  const page: PageSize = {
    width: element.offsetWidth + padding.left + padding.right,
    height: element.offsetHeight + padding.top + padding.bottom,
  };
  const framed = onSurface || !!source.exportPadding;
  return from(import('@zumer/snapdom')).pipe(
    switchMap(({ snapdom }) => snapdom(element, { dpr, cache: 'disabled', embedFonts: true })),
    switchMap((capture: CaptureResult) => capture.toCanvas()),
    switchMap((canvas: HTMLCanvasElement) =>
      render(framed ? onSurfaceOf(canvas, padding, surfaceColor(element) ?? '#ffffff', dpr) : canvas, page)),
  );
}

function onSurfaceOf(canvas: HTMLCanvasElement, padding: ExportPadding, surface: string, dpr: number): HTMLCanvasElement {
  const framed = document.createElement('canvas');
  framed.width = canvas.width + (padding.left + padding.right) * dpr;
  framed.height = canvas.height + (padding.top + padding.bottom) * dpr;
  const context = framed.getContext('2d');
  if (!context) {
    return canvas;
  }
  context.fillStyle = surface;
  context.fillRect(0, 0, framed.width, framed.height);
  context.drawImage(canvas, padding.left * dpr, padding.top * dpr);
  return framed;
}

function canvasBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob(
    (blob: Blob | null) => blob ? resolve(blob) : reject(new Error('The capture could not be encoded as PNG.')),
    'image/png',
  ));
}

@Injectable()
export class SvgDownloadStrategy implements DownloadStrategy {
  readonly fileType: DownloadFormat = 'svg';

  supports(source: ExportSource): boolean {
    return typeof (source as SvgExportSource).getExportSvg === 'function';
  }

  createBlob(source: ExportSource): Observable<Blob> {
    const svg = (source as SvgExportSource).getExportSvg();
    if (!svg) {
      return throwError(() => new Error('No source svg provided.'));
    }
    return of(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  }
}

@Injectable()
export class PdfDownloadStrategy implements DownloadStrategy {
  readonly fileType: DownloadFormat = 'pdf';

  supports(source: ExportSource): boolean {
    return typeof (source as PdfExportSource).getPdfBlob === 'function';
  }

  createBlob(source: ExportSource): Observable<Blob> {
    return (source as PdfExportSource).getPdfBlob();
  }
}

@Injectable()
export class CsvDownloadStrategy implements DownloadStrategy {
  readonly fileType: DownloadFormat = 'csv';

  supports(source: ExportSource): boolean {
    return typeof (source as CsvExportSource).getCsvBlob === 'function';
  }

  createBlob(source: ExportSource): Observable<Blob> {
    return (source as CsvExportSource).getCsvBlob();
  }
}

@Injectable()
export class DocxDownloadStrategy implements DownloadStrategy {
  readonly fileType: DownloadFormat = 'docx';

  supports(source: ExportSource): boolean {
    return typeof (source as DocxExportSource).getDocxBlob === 'function';
  }

  createBlob(source: ExportSource): Observable<Blob> {
    return (source as DocxExportSource).getDocxBlob();
  }
}

